import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { type Observable, of } from 'rxjs';

/**
 * Service for handling server-side error message translations.
 *
 * This service relies on the "server-codes" scope being eagerly loaded
 * during app initialization. Make sure to mark the "server-codes" translation
 * scope as eager (eager: true) in your translation database.
 *
 * Usage:
 * 1. Create a translation entry with scope "server-codes" and eager: true
 * 2. Add server error codes as keys (e.g., "ERR_USER_NOT_FOUND": "User not found")
 * 3. The service will automatically use the pre-loaded translations
 */
@Injectable({
  providedIn: 'root'
})
export class ServerMessageService {
  private readonly translate = inject(TranslateService);

  private readonly SCOPE_PREFIX = 'SERVER_CODES.';

  /**
   * Retrieves the translated message for a server error code.
   *
   * @param messageCode - The server error code (e.g., "ERR_USER_NOT_FOUND")
   * @returns Observable with the translated message or the original code if not found
   */
  getServerMessage(messageCode: string | undefined): Observable<string> {
    if (!messageCode) {
      return of('');
    }

    // Use the translation key format: SERVER_CODES.{messageCode}
    const translationKey = `${this.SCOPE_PREFIX}${messageCode}`;

    // Get the translated value using instant() since server-codes is eagerly loaded
    const translatedMessage = this.translate.instant(translationKey);

    // If translation is not found, instant() returns the key itself
    // In that case, return the original message code as fallback
    const result = translatedMessage === translationKey ? messageCode : translatedMessage;

    return of(result);
  }

  /**
   * Synchronously retrieves the translated message for a server error code.
   * Useful when you need the value immediately without subscribing.
   *
   * @param messageCode - The server error code
   * @returns The translated message or the original code if not found
   */
  getServerMessageSync(messageCode: string | undefined): string {
    if (!messageCode) {
      return '';
    }

    const translationKey = `${this.SCOPE_PREFIX}${messageCode}`;
    const translatedMessage = this.translate.instant(translationKey);

    return translatedMessage === translationKey ? messageCode : translatedMessage;
  }
}
